import { addQueryParams } from '@arcadiapower/warbler';
import { assign, createMachine } from 'xstate';
import { UnparsedQueryParams } from '~/providers/query-params';
import { createFunnelPacket, fetchExistingUtilityAccounts } from '~/services';
import { INTERNAL_ROUTES } from '~/utils/constants';
import { hardRedirect } from '~/utils/for-warbler';
import { sendToSegment } from '~/utils/tracking';
import { parseServiceError } from '../utils/actions';
import {
  UtilityAccountSelectionContext,
  UtilityAccountSelectionEvent,
  UtilityAccountSelectionParams,
  UtilityAccountSelectionServices,
} from './types';

export const utilityAccountSelectionMachine = (params: UtilityAccountSelectionParams) =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as UtilityAccountSelectionContext,
        events: {} as UtilityAccountSelectionEvent,
        services: {} as UtilityAccountSelectionServices,
      },
      context: {
        userId: -1,
        accountId: -1,
        selectedUtilityAccountId: -1,
        utilityAccounts: [],
        queryParams: {},
      },
      id: params.id,
      initial: 'selectUtilityAccount',
      entry: 'setContextIds',
      states: {
        selectUtilityAccount: {
          id: 'selectUtilityAccount',
          initial: 'fetching',
          states: {
            fetching: {
              entry: 'trackStarted',
              invoke: {
                id: 'fetchExistingUtilityAccounts',
                src: 'fetchExistingUtilityAccounts',
                onDone: { target: 'idle', actions: 'saveUtilityAccounts' },
                onError: '#unexpected',
              },
            },
            idle: {
              on: {
                SELECT_UTILITY_ACCOUNT: {
                  target: 'submitting',
                  actions: 'saveUtilityAccountSelection',
                },
                SELECT_NEW_UTILITY_ACCOUNT: '#createUtilityAccount',
              },
            },
            submitting: {
              entry: 'trackUtilityAccountChosen',
              always: [
                { cond: 'hasCompletedSolarSignUp', target: '#dashboard' },
                { target: '#funnel' },
              ],
            },
          },
        },
        createUtilityAccount: {
          id: 'createUtilityAccount',
          initial: 'idle',
          entry: 'trackStartedCreation',
          states: {
            idle: {
              on: {
                CREATE_UTILITY_ACCOUNT: {
                  target: 'submitting',
                  actions: 'saveNewUtilitySubmission',
                },
                GO_BACK: '#selectUtilityAccount.idle',
              },
            },
            submitting: {
              invoke: {
                src: 'createUtilityAccount',
                id: 'createUtilityAccount',
                onDone: {
                  target: '#funnel',
                  actions: ['saveNewUtilityAccountSelection', 'trackFinishedCreation'],
                },
                onError: { target: 'idle', actions: 'setError' },
              },
            },
          },
        },
        dashboard: {
          id: 'dashboard',
          on: {
            GO_BACK: { target: 'selectUtilityAccount.idle' },
          },
        },
        funnel: {
          id: 'funnel',
          entry: 'redirectToFunnel',
        },
        unexpected: {
          id: 'unexpected',
        },
      },
    },
    {
      actions: {
        setError: assign((_context, event) => ({
          error: parseServiceError(event),
        })),
        setContextIds: assign(context => ({
          userId: context.queryParams.resumingUser!.id,
          accountId: context.queryParams.resumingUser!.packet!.account!.id,
        })),
        saveNewUtilitySubmission: assign((_context, event) => ({
          newUtility: { utilityId: event.data.utilityId, zipCode: event.data.zipCode },
        })),
        saveNewUtilityAccountSelection: assign((_context, event) => ({
          selectedUtilityAccountId: event.data.utilityAccountId,
        })),
        saveUtilityAccountSelection: assign((_context, event) => ({
          selectedUtilityAccountId: event.data.selectedUtilityAccountId,
        })),
        saveUtilityAccounts: assign((_context, event) => ({
          utilityAccounts: event.data.utilityAccounts,
        })),
        redirectToFunnel: context => {
          const resumingQueryParams =
            context?.queryParams?.resumingUser?.packet?.user?.originationSource?.queryParams;

          const queryParams: UnparsedQueryParams = {
            userId: context.userId,
            selectedUtilityAccountId: context.selectedUtilityAccountId,
            enrollmentOption:
              context.queryParams.enrollmentOption ||
              context.queryParams.unparsedQueryParams?.enrollmentOption,
          };

          if (resumingQueryParams) {
            queryParams.preferredEnrollment = resumingQueryParams.preferredEnrollment;
            queryParams.preferredBillingExperience = resumingQueryParams.preferredBillingExperience;
            queryParams.projectId =
              context?.queryParams?.resumingUser?.packet?.user?.originationSource
                ?.funnelVnmProjectId ?? undefined;
          }

          const redirectPath = addQueryParams(INTERNAL_ROUTES.RESUMING_REDIRECT, queryParams);
          hardRedirect(redirectPath);
        },
        trackUtilityAccountChosen: context => {
          sendToSegment({
            type: 'utility_account_selection_chosen',
            selectedUtilityAccountId: context.selectedUtilityAccountId!,
            userId: context.userId,
          });
        },
        trackStarted: context => {
          sendToSegment({
            type: 'utility_account_selection_step_started',
            userId: context.userId,
          });
        },
        trackStartedCreation: context => {
          sendToSegment({
            type: 'utility_account_creation_step_started',
            userId: context.userId,
          });
        },
        trackFinishedCreation: (context, event) => {
          sendToSegment({
            type: 'utility_account_creation_step_completed',
            userId: context.userId,
            utilityId: context.newUtility!.utilityId,
            utilityAccountId: event.data.utilityAccountId,
          });
        },
      },
      services: {
        fetchExistingUtilityAccounts: async context => {
          const { utilityAccounts } = await fetchExistingUtilityAccounts({
            userId: context.userId,
          });
          return { utilityAccounts };
        },

        createUtilityAccount: async context => {
          /**
           * when resuming, if the user enter the same zip and utility as before,
           * just use the same utilityAccountId.
           */
          if (
            context.newUtility?.zipCode === context.queryParams.resumingUser?.packet.user.zipCode &&
            context.newUtility?.utilityId === context.queryParams.resumingUser?.packet.utility.id
          ) {
            return {
              utilityAccountId: context.queryParams.resumingUser?.packet?.utilityAccount?.id!,
            };
          }
          const { utilityAccount } = await createFunnelPacket({
            accountId: context.accountId,
            utilityId: context.newUtility!.utilityId,
          });
          return { utilityAccountId: utilityAccount.id };
        },
      },
      guards: {
        hasCompletedSolarSignUp: context => {
          const paymentMethodId =
            context.queryParams.resumingUser?.packet.customPlan.paymentMethodId;
          const billingExperienceType =
            context.queryParams.resumingUser?.packet.planTemplate?.billingExperienceType;
          const isUcb = billingExperienceType === 'ucb';
          const paymentNeeded = !isUcb && paymentMethodId == null;
          const utilityAccount = context.utilityAccounts.find(
            ua => ua.id === context.selectedUtilityAccountId,
          );
          return !paymentNeeded && !!utilityAccount?.hasVnmProduct;
        },
      },
    },
  );
